import $ from 'jQuery';
import { selectOption, baseComponent, selectComponent } from './base_components.js';
import './config_pneumatic_components.css';

/**
 * @typedef {*} AxisComponentState
 * @prop {*} config Config the axis belongs to.
 * @prop {*} axis The axis object
 *
 */

/**
 *
 * @typedef {Object} ConfigPneumaticParams
 * @prop {string} commandName
 * @prop {string} valveTitle
 * @prop {JQuery<HTMLElement> | JQuery<HTMLElement>[]} valveOptions
 * @prop {string} valveInitValue
 * @prop {string} [valveTooltip]
 * @prop {(newValve: string) => void} valveOnChange
 * @prop {string} switchTitle
 * @prop {JQuery<HTMLElement> | JQuery<HTMLElement>[]} switchOptions
 * @prop {string} switchInitValue
 * @prop {string} [switchTooltip]
 * @prop {(newSwitch: string) => void} switchOnChange
 *
 * @param {ConfigPneumaticParams} pParams
  * @returns {{
 *   widget: JQuery<HTMLElement>,
 *   getValve: Function
 *   getSwitch: Function
 * }}
 */

function configPneumaticIOComponent(pParams) {
  const {
    commandName,
    valveTitle,
    valveOptions,
    valveTooltip,
    valveInitValue,
    valveOnChange,
    switchTitle,
    switchTooltip,
    switchInitValue,
    switchOnChange,
    switchOptions,
  } = pParams;
  const lContainer = baseComponent(commandName);
  const lValve = selectComponent(valveTitle, valveOptions, valveInitValue, valveTooltip, valveOnChange).widget;
  const lSwitch = selectComponent(switchTitle, switchOptions, switchInitValue, switchTooltip, switchOnChange).widget;
  const lCommandContainer = $('<div>').addClass("ml_pneumatic_commands_container");
  lValve.css({ 'justify-content': 'flex-end' });
  lContainer.addClass('v_layout');
  lCommandContainer.append(lValve, lSwitch);
  lContainer.append(lCommandContainer);

  return {
    widget: lContainer,
    getValve: () => lValve,
    getSwitch: () => lSwitch
  };
}

/**
 * @param {AxisComponentState} state
 * @returns {JQuery<HTMLElement>}
 */
function controlDeviceComponent({ axis, config }) {

  /**
   * @param {Number} pDeviceNumber The device number to test if it should be disabled
   * @param {Number} pSelectedDevice The selected device number
   * @returns {Boolean}
   */
  const isDeviceDisabled = (pDeviceNumber, pSelectedDevice) => {
    // The selected option should not be disabled
    if (pDeviceNumber === pSelectedDevice) { return false; }

    const lDeviceTypeMap = config.mDeviceTypeMap;
    const lOutputMap = config.mOutputMap;
    const lInputMap = config.mInputMap;
    const lValidTypes = ['?', 'digitalIOModule'];

    if (lValidTypes.includes(lDeviceTypeMap[pDeviceNumber])) {
      const lHasEnoughAvailableOutputPins = Object.values(lOutputMap[pDeviceNumber]).filter((isOccupied) => !isOccupied).length >= 2;
      const lHasEnoughAvailableInputPins = Object.values(lInputMap[pDeviceNumber]).filter((isOccupied) => !isOccupied).length >= 2;
      // Bag gripper requires inputs and outputs
      if (axis.isBagGripper()) {
        if (lHasEnoughAvailableOutputPins && lHasEnoughAvailableInputPins) { return false; }
      }
      // Other pneumatics only require outputs
      else {
        if (lHasEnoughAvailableOutputPins) { return false; }
      }
    }
    return true;
  };

  /**
   * Converts a list of devices to a list of select menu options
   * @param {Number[]} pAllDevices A list of device numbers
   * @param {Number} pSelectedDevice The selected device number
   * @returns {JQuery<HTMLElement>[]}
   */
  const deviceListToMenuOptions = (pAllDevices, pSelectedDevice) => {
    return pAllDevices.map((pDeviceNumber) => selectOption(pDeviceNumber.toString(), `Digital IO ${pDeviceNumber}`, isDeviceDisabled(pDeviceNumber, pSelectedDevice)));
  };

  const lSelectedDevice = axis.getDevice();
  const lControlDeviceSelect = selectComponent(
    'Control Device',
    deviceListToMenuOptions(axis.getDeviceRange(), lSelectedDevice),
    lSelectedDevice,
    'Control Module IO',
    (pValue) => axis.setDevice(parseInt(pValue))
  ).widget;
  return lControlDeviceSelect;
}

/**
 * @param {*} pConfig
 * @param {*} pAxis
 * @param {string} pSelectedValve
 * @param {string} pSelectedSwitch
 * @returns {{valveOptions: JQuery<HTMLElement>[], switchOptions: JQuery<HTMLElement>[]}}
 */
const getPneumaticPinMenuOptions = (pConfig, pAxis, pSelectedValve, pSelectedSwitch) => {
  const lPinRange = pAxis.getPinRange();
  const lDeviceInputOccupencyMap = pConfig.mInputMap[pAxis.getDevice()];
  const lDeviceOutputOccupencyMap = pConfig.mOutputMap[pAxis.getDevice()];
  const lAvailableInputs = Object.keys(lDeviceInputOccupencyMap).filter(pPin => !lDeviceInputOccupencyMap[pPin]);
  const lAvailableOutputs = Object.keys(lDeviceOutputOccupencyMap).filter(pPin => !lDeviceOutputOccupencyMap[pPin]);


  const lValveSelectOptions = lPinRange.map((pPin) => selectOption(pPin, `Out ${pPin}`, pPin != pSelectedValve && !lAvailableOutputs.includes(pPin.toString())));
  let lSwitchSelectOptions = lPinRange.map((pPin) => selectOption(pPin, `In ${pPin}`, pPin != pSelectedSwitch && !lAvailableInputs.includes(pPin.toString())));
  if (!pAxis.isBagGripper()) {
    lSwitchSelectOptions = [selectOption('null', 'None', false), ...lSwitchSelectOptions];
  }
  return { valveOptions: lValveSelectOptions, switchOptions: lSwitchSelectOptions };
};

/**
 * @param {AxisComponentState} state
 * @returns {JQuery<HTMLElement>}
 */
function pushComponent({ axis, config }) {

  const onValveChange = (pValue) => { axis.setPin('pushOutPin', parseInt(pValue)); };
  const onSwitchChange = (pValue) => {
    if (pValue === 'null') { axis.setPin('pushInPin', 'null'); }
    else { axis.setPin('pushInPin', parseInt(pValue)); }
  };

  const lSelectedValve = axis.getPin('pushOutPin');
  const lSelectedSwitch = axis.getPin('pushInPin') == null ? 'null' : axis.getPin('pushInPin');
  const { valveOptions, switchOptions } = getPneumaticPinMenuOptions(config, axis, lSelectedValve, lSelectedSwitch);

  const lPushWidget = configPneumaticIOComponent({
    commandName: axis.isBagGripper() ? 'Open' : 'Push',
    valveTitle: 'Valve A Pin',
    valveOptions,
    valveTooltip: axis.isBagGripper() ? 'Output channel to open the gripper' : 'Output channel to push the cylinder',
    valveInitValue: lSelectedValve,
    valveOnChange: onValveChange,
    switchTitle: 'Position Sensor',
    switchOptions,
    switchTooltip: axis.isBagGripper() ? 'Sensor to read gripper position' : 'Sensor to read cylinder position',
    switchInitValue: lSelectedSwitch,
    switchOnChange: onSwitchChange
  }).widget;

  return lPushWidget;
}

/**
 * @param {AxisComponentState} state
 * @returns {JQuery<HTMLElement>}
 */
function pullComponent({ axis, config }) {

  const onValveChange = (pValue) => {
    axis.setPin('pullOutPin', parseInt(pValue));
  };
  const onSwitchChange = (pValue) => {
    if (pValue === 'null') { axis.setPin('pullInPin', pValue); }
    else { axis.setPin('pullInPin', parseInt(pValue)); }
  };

  const lSelectedValve = axis.getPin('pullOutPin');
  const lSelectedSwitch = axis.getPin('pullInPin') == null ? 'null' : axis.getPin('pullInPin');

  const { valveOptions, switchOptions } = getPneumaticPinMenuOptions(config, axis, lSelectedValve, lSelectedSwitch);

  const lPullWidget = configPneumaticIOComponent({
    commandName: axis.isBagGripper() ? 'Close' : 'Pull',
    valveTitle: 'Valve B Pin',
    valveOptions,
    valveTooltip: axis.isBagGripper() ? 'Output channel to close the gripper' : 'Output channel to pull the cylinder',
    valveInitValue: lSelectedValve,
    valveOnChange: onValveChange,
    switchTitle: 'Position Sensor',
    switchOptions,
    switchTooltip: axis.isBagGripper() ? 'Sensor to read gripper position' : 'Sensor to read cylinder position',
    switchInitValue: lSelectedSwitch,
    switchOnChange: onSwitchChange,
  }).widget;

  return lPullWidget;
}

/**
 * @param {AxisComponentState} state
 * @returns {JQuery<HTMLElement>}
 */
const vfdForwardReverseComponent = ({axis, config}) => {
  const lPinRange = axis.getPinRange();
  const lDeviceOutputOccupencyMap = config.mOutputMap[axis.getDevice()];
  const lAvailableOutputs = Object.keys(lDeviceOutputOccupencyMap).filter(pPin => !lDeviceOutputOccupencyMap[pPin]);
  const lSelectedForwardReverse = axis.getPin('pullOutPin');
  const lForwardReverseOptions = lPinRange.map((pPin) => selectOption(pPin, `Out ${pPin}`, pPin != lSelectedForwardReverse && !lAvailableOutputs.includes(pPin.toString())));

  const onForwardReverseChange = (pValue) => {
    axis.setPin('pullOutPin', parseInt(pValue)); // forward reverse
  };

  const lForwardReverseSelect = selectComponent(
    'Pin',
    lForwardReverseOptions,
    lSelectedForwardReverse,
    'Output pin for motor direction',
    onForwardReverseChange
  );
  lForwardReverseSelect.widget.addClass("axis_config_ac_motor_output");

  const lForwardReverseContainer = baseComponent(null);
  const lForwardReverseLabel = $('<h5>').addClass('ml_component_label').text('Forward/Reverse');
  lForwardReverseContainer.append(lForwardReverseLabel, lForwardReverseSelect.widget);
  return lForwardReverseContainer;
};

/**
 * @param {AxisComponentState} state
 * @returns {JQuery<HTMLElement>}
 */
const vfdOnOffComponent = ({axis, config}) => {
  const lPinRange = axis.getPinRange();
  const lDeviceOutputOccupencyMap = config.mOutputMap[axis.getDevice()];
  const lAvailableOutputs = Object.keys(lDeviceOutputOccupencyMap).filter(pPin => !lDeviceOutputOccupencyMap[pPin]);
  const lSelectedOnOff = axis.getPin('pushOutPin');
  const lOnOffOptions = lPinRange.map((pPin) => selectOption(pPin, `Out ${pPin}`, pPin != lSelectedOnOff && !lAvailableOutputs.includes(pPin.toString())));

  const onOnOffChange = (pValue) => {
    axis.setPin('pushOutPin', parseInt(pValue)); // on off
  };

  const lOnOffSelect = selectComponent(
    'Pin',
    lOnOffOptions,
    lSelectedOnOff,
    'Output pin for ON or OFF',
    onOnOffChange
  );

  lOnOffSelect.widget.addClass("axis_config_ac_motor_output");
  const lOnOffContainer = baseComponent(null);
  const lOnOffLabel = $('<h5>').addClass('ml_component_label').text('On/Off');
  lOnOffContainer.append(lOnOffLabel, lOnOffSelect.widget);

  return lOnOffContainer;
};

export {
  controlDeviceComponent,
  pushComponent,
  pullComponent,
  vfdForwardReverseComponent,
  vfdOnOffComponent
};